import { DomainError } from "./errors";
import { applyPct, mulQ4, toSafe } from "./arith";
import { allocateProportional } from "./commission";
import { BRAZIL_UFS, isUf, type Uf } from "./dashboard";
import { MAX_PRICE_CENTS } from "./pricing";
import { MAX_DOCUMENT_CENTS } from "./quotes";
import { MAX_STOCK_QTY_Q4 } from "./stock";

// ===== Motor de impostos da NF-e (RF-VEN-04; 05 §8) =====
//
// Função pura e determinística: empresa, destinatário, regras da natureza de operação, itens e valores do documento →
// cada item no formato do leiaute da NF-e (CFOP, ICMS/ST/FCP, ICMSUFDest, IPI, PIS, COFINS, IBSCBS, IS) + totais.
// Dinheiro em centavos, percentuais em pct4 (12,5% = 125_000; 100% = 1_000_000), quantidade em q4.
//
// Arredondamento: comercial (meio para cima, 05 §1), por item e por campo, na ordem do leiaute — cada valor sai do
// campo anterior já em centavos (vICMS = round(vBC × pICMS) com o vBC arredondado), que é a conta que a SEFAZ confere.
// Totais = Σ dos itens (nunca recalculados sobre a soma). Contas em BigInt (applyPct/mulQ4 e `roundDiv` abaixo).
// Os nomes dos campos são descritivos; o JSDoc traz a tag do leiaute para o mapeamento da T2.3 (Nuvem Fiscal).

export const MAX_TAX_ITEMS = 200;
/** MVA pode passar de 100% (ex.: bebidas): teto de 1.000%. */
export const MAX_MVA_PCT4 = 10_000_000;
const HUNDRED = 1_000_000;
const HUNDRED_N = 1_000_000n;

export const EXTERIOR = "EX" as const;
export type DestUf = Uf | typeof EXTERIOR;
export type Crt = 1 | 2 | 3 | 4;
export type TaxRegime = "simples" | "mei" | "presumido" | "real";
/** indIEDest: 1 contribuinte, 2 contribuinte isento de inscrição, 9 não contribuinte. */
export type IeIndicator = 1 | 2 | 9;
export type TaxDirection = "out" | "in";
/** idDest: 1 interna, 2 interestadual, 3 exterior. */
export type Destination = 1 | 2 | 3;

export const CRTS: readonly Crt[] = [1, 2, 3, 4];
export const IE_INDICATORS: readonly IeIndicator[] = [1, 2, 9];
export const isSimplesCrt = (crt: Crt) => crt === 1 || crt === 4;
/** CRT 2 (excesso de sublimite) segue no Simples para os tributos federais (PIS/COFINS sem destaque). */
const federalSimples = (crt: Crt) => crt !== 3;

/**
 * Alíquota interna modal do ICMS por UF (sem FCP) — tabela de **referência** (out/2026) usada quando a regra não traz a
 * alíquota (operação interna, ST e DIFAL). Confirme com o contador; a regra da natureza sempre prevalece.
 */
export const ICMS_INTERNAL_RATE_PCT4: Readonly<Record<Uf, number>> = {
  AC: 190_000,
  AL: 190_000,
  AM: 200_000,
  AP: 180_000,
  BA: 205_000,
  CE: 200_000,
  DF: 200_000,
  ES: 170_000,
  GO: 190_000,
  MA: 230_000,
  MG: 180_000,
  MS: 170_000,
  MT: 170_000,
  PA: 190_000,
  PB: 200_000,
  PE: 205_000,
  PI: 225_000,
  PR: 195_000,
  RJ: 200_000,
  RN: 200_000,
  RO: 195_000,
  RR: 200_000,
  RS: 170_000,
  SC: 170_000,
  SE: 190_000,
  SP: 180_000,
  TO: 200_000,
};

/** Alíquotas de teste de 2026 da reforma (LC 214/2025): CBS 0,9%, IBS UF 0,1%, IBS Município 0%. */
export const REFORM_2026_RATES = { cbsPct4: 9_000, ibsUfPct4: 1_000, ibsMunPct4: 0 } as const;

const SOUTH_SOUTHEAST_EXCEPT_ES: ReadonlySet<Uf> = new Set(["SP", "RJ", "MG", "PR", "SC", "RS"]);
/** Origens com alíquota interestadual de 4% (Res. SF 13/2012): importado e conteúdo de importação > 40%. */
const IMPORTED_ORIGINS: ReadonlySet<number> = new Set([1, 2, 3, 8]);

/**
 * Alíquota interestadual legal: 4% para origem 1, 2, 3 e 8 (Res. SF 13/2012); 7% de S/SE (exceto ES) para N, NE, CO e
 * ES; 12% nos demais casos (Res. SF 22/1989).
 */
export function interstateRatePct4(from: Uf, to: Uf, origin: number): number {
  if (IMPORTED_ORIGINS.has(origin)) return 40_000;
  if (SOUTH_SOUTHEAST_EXCEPT_ES.has(from) && !SOUTH_SOUTHEAST_EXCEPT_ES.has(to)) return 70_000;
  return 120_000;
}

export function destinationOf(companyUf: Uf, destUf: DestUf): Destination {
  if (destUf === EXTERIOR) return 3;
  return destUf === companyUf ? 1 : 2;
}

/**
 * CFOP da regra com o primeiro dígito pelo destino (saída 5/6/7, entrada 1/2/3). Interestadual para não contribuinte:
 * 6101→6107 e 6102→6108; mercadoria com ST retido anteriormente (5405) sem par 6405 vira 6404.
 */
export function adjustCfop(
  cfop: string,
  direction: TaxDirection,
  dest: Destination,
  ie: IeIndicator,
): string {
  const first = direction === "out" ? ["5", "6", "7"][dest - 1]! : ["1", "2", "3"][dest - 1]!;
  let c = first + cfop.slice(1);
  if (direction === "out" && dest === 2) {
    if (c === "6405") c = "6404";
    if (ie === 9 && c === "6101") c = "6107";
    if (ie === 9 && c === "6102") c = "6108";
  }
  return c;
}

// ---------- tipos de entrada ----------

export type TaxCompany = { crt: Crt; uf: Uf; taxRegime: TaxRegime | null };
export type TaxRecipient = { uf: DestUf; ieIndicator: IeIndicator; finalConsumer: boolean };

/** Condições da regra (null = qualquer). */
export type TaxRuleConditions = {
  destUf: DestUf | null;
  destIeIndicator: IeIndicator | null;
  destFinalConsumer: boolean | null;
  crt: Crt | null;
  ncmPrefix: string | null;
  productId: string | null;
  productGroupId: string | null;
  productOrigin: number | null;
};

/** Resultado da regra (null = padrão do motor). Percentuais em pct4. */
export type TaxRuleResult = {
  cfop: string;
  icmsCst: string | null;
  icmsCsosn: string | null;
  /** pICMS interno (a interestadual é sempre a legal). */
  icmsPct4: number | null;
  /** pRedBC. */
  icmsReductionPct4: number | null;
  /** pCredSN (CSOSN 101/201/900). */
  icmsCreditSnPct4: number | null;
  /** pDif (CST 51). */
  icmsDeferralPct4: number | null;
  /** pFCP (só operação interna). */
  fcpPct4: number | null;
  /** modBCST: 4 = MVA, 6 = valor da operação. */
  icmsStModBc: 4 | 6 | null;
  icmsStMvaPct4: number | null;
  /** MVA ajustada (Conv. ICMS 142/2018 cl. 13ª) na operação interestadual. */
  icmsStMvaAdjust: boolean;
  /** pICMSST (alíquota interna do destino, sem FCP). */
  icmsStPct4: number | null;
  icmsStReductionPct4: number | null;
  fcpStPct4: number | null;
  /** DIFAL (EC 87) quando a operação pede; false desliga. */
  difal: boolean;
  difalDoubleBase: boolean;
  /** pICMSUFDest (sem FCP). */
  icmsDestPct4: number | null;
  /** pFCPUFDest. */
  fcpDestPct4: number | null;
  ipiCst: string | null;
  ipiPct4: number | null;
  ipiEnq: string | null;
  pisCst: string | null;
  pisPct4: number | null;
  cofinsCst: string | null;
  cofinsPct4: number | null;
  /** Exclui o ICMS destacado da base do PIS/COFINS (RE 574.706). */
  pisCofinsExcludeIcms: boolean;
  ibsCst: string | null;
  /** cClassTrib. */
  ibsClass: string | null;
  ibsUfPct4: number | null;
  ibsMunPct4: number | null;
  cbsPct4: number | null;
  /** pRedAliq do IBS (UF e município) e da CBS (CST 200). */
  ibsReductionPct4: number | null;
  cbsReductionPct4: number | null;
  /** pDif (CST 510). */
  ibsDeferralPct4: number | null;
  isPct4: number | null;
  infoText: string | null;
};

export type TaxRule = TaxRuleConditions &
  TaxRuleResult & { id: string; priority: number; active: boolean; createdAt?: string };

/** Valores por UNIDADE do ST retido anteriormente (cadastro do produto). */
export type TaxStRetained = { baseCents: number; stCents: number; substituteOwnCents: number };

export type TaxItemInput = {
  productId: string | null;
  productGroupId: string | null;
  ncm: string | null;
  cest: string | null;
  /** orig (0–8). */
  origin: number;
  qtyQ4: number;
  /** vUnCom. vProd = round(vUnCom × qCom). */
  unitPriceCents: number;
  /** Desconto do próprio item (o do documento é rateado e somado). */
  discountCents: number;
  /** IPI por unidade (pauta) quando a regra não tem alíquota. */
  ipiFixedCents: number | null;
  /** % aproximado de tributos (Lei 12.741). */
  approxTaxPct4: number | null;
  stRetained: TaxStRetained | null;
  /** cClassTrib do produto: refina a regra de tributação integral (CST 000). */
  ibsCbsClass: string | null;
};

export type TaxInput = {
  company: TaxCompany;
  recipient: TaxRecipient;
  direction: TaxDirection;
  rules: readonly TaxRule[];
  items: readonly TaxItemInput[];
  /** Valores do documento rateados pelos itens. */
  freightCents: number;
  insuranceCents: number;
  otherCents: number;
  discountCents: number;
};

// ---------- tipos de saída ----------

export type StResult = {
  /** modBCST. */
  modBc: 4 | 6;
  /** pMVAST (já ajustada quando for o caso). */
  mvaPct4: number;
  mvaAdjusted: boolean;
  /** pRedBCST. */
  reductionPct4: number;
  /** vBCST. */
  baseCents: number;
  /** pICMSST. */
  ratePct4: number;
  /** vICMSST. */
  valueCents: number;
  /** ICMS próprio deduzido (real no normal; hipotético no Simples). Informativo. */
  ownDeductedCents: number;
  /** vBCFCPST, pFCPST, vFCPST. */
  fcpBaseCents: number;
  fcpPct4: number;
  fcpCents: number;
};

export type StRetainedResult = {
  /** vBCSTRet. */
  baseCents: number;
  /** pST (ICMS-ST + próprio do substituto sobre a base). */
  ratePct4: number;
  /** vICMSSubstituto. */
  substituteOwnCents: number;
  /** vICMSSTRet. */
  valueCents: number;
};

export type IcmsResult = {
  /** Grupo do leiaute (ICMS00…ICMS90, ICMSSN101…ICMSSN900). */
  group: string;
  /** orig. */
  origin: number;
  cst: string | null;
  csosn: string | null;
  /** modBC (3 = valor da operação); null quando o grupo não tem base própria. */
  modBc: 3 | null;
  /** vBC. */
  baseCents: number;
  /** pRedBC. */
  reductionPct4: number;
  /** pICMS. */
  ratePct4: number;
  /** vICMSOp (CST 51). */
  operationCents: number;
  /** pDif, vICMSDif (CST 51). */
  deferralPct4: number;
  deferredCents: number;
  /** vICMS. */
  valueCents: number;
  /** vBCFCP, pFCP, vFCP. */
  fcpBaseCents: number;
  fcpPct4: number;
  fcpCents: number;
  /** pCredSN, vCredICMSSN. */
  creditSnPct4: number;
  creditSnCents: number;
  st: StResult | null;
  stRetained: StRetainedResult | null;
};

/** Grupo ICMSUFDest (DIFAL). */
export type DifalResult = {
  /** vBCUFDest. */
  baseCents: number;
  /** vBCFCPUFDest. */
  fcpBaseCents: number;
  /** pFCPUFDest. */
  fcpPct4: number;
  /** pICMSUFDest. */
  destRatePct4: number;
  /** pICMSInter (4, 7 ou 12%). */
  interRatePct4: number;
  /** pICMSInterPart (100% desde 2019). */
  interPartPct4: number;
  /** vFCPUFDest. */
  fcpCents: number;
  /** vICMSUFDest. */
  destCents: number;
  /** vICMSUFRemet (0 desde 2019). */
  originCents: number;
  doubleBase: boolean;
};

export type IpiResult = {
  /** IPITrib (tributado) ou IPINT. */
  group: "IPITrib" | "IPINT";
  cst: string;
  /** cEnq. */
  enq: string;
  /** vBC e pIPI (por alíquota) ou qUnid e vUnid (por unidade). */
  baseCents: number;
  ratePct4: number;
  qtyQ4: number;
  unitCents: number;
  /** vIPI. */
  valueCents: number;
};

export type PisCofinsResult = {
  /** PISAliq/COFINSAliq, PISNT/COFINSNT ou PISOutr/COFINSOutr. */
  group: "Aliq" | "NT" | "Outr";
  cst: string;
  baseCents: number;
  ratePct4: number;
  valueCents: number;
};

export type IbsCbsPart = {
  /** pIBSUF / pIBSMun / pCBS. */
  ratePct4: number;
  /** gRed: pRedAliq e pAliqEfet. */
  reductionPct4: number;
  effectivePct4: number;
  /** gDif: pDif e vDif. */
  deferralPct4: number;
  deferredCents: number;
  /** vIBSUF / vIBSMun / vCBS. */
  valueCents: number;
};

/** Grupo IBSCBS (NT 2025.002). */
export type IbsCbsResult = {
  /** CST (3 dígitos). */
  cst: string;
  /** cClassTrib (6 dígitos). */
  classCode: string;
  /** gIBSCBS presente (false nos CSTs sem valores: 400, 410, 550). */
  hasValues: boolean;
  /** vBC. */
  baseCents: number;
  ibsUf: IbsCbsPart;
  ibsMun: IbsCbsPart;
  /** vIBS = vIBSUF + vIBSMun. */
  ibsCents: number;
  cbs: IbsCbsPart;
};

export type IsResult = { baseCents: number; ratePct4: number; valueCents: number };

export type TaxItemResult = {
  index: number;
  ruleId: string;
  /** idDest/indFinal efetivos (iguais em todos os itens). */
  cfop: string;
  /** vProd, vDesc (item + rateio), vFrete, vSeg, vOutro. */
  grossCents: number;
  discountCents: number;
  freightCents: number;
  insuranceCents: number;
  otherCents: number;
  icms: IcmsResult;
  difal: DifalResult | null;
  ipi: IpiResult | null;
  pis: PisCofinsResult;
  cofins: PisCofinsResult;
  ibsCbs: IbsCbsResult | null;
  is: IsResult | null;
  /** vTotTrib (Lei 12.741). */
  approxTaxCents: number;
  /** Contribuição do item ao vNF: vProd − vDesc + vFrete + vSeg + vOutro + vST + vFCPST + vIPI. */
  totalCents: number;
  /** infAdProd (texto legal da regra). */
  infoText: string | null;
  warnings: string[];
};

/** ICMSTot + IBSCBSTot (+ IS). */
export type TaxTotals = {
  baseCents: number;
  icmsCents: number;
  fcpCents: number;
  stBaseCents: number;
  stCents: number;
  fcpStCents: number;
  difalCents: number;
  difalFcpCents: number;
  difalOriginCents: number;
  productsCents: number;
  freightCents: number;
  insuranceCents: number;
  discountCents: number;
  otherCents: number;
  ipiCents: number;
  pisCents: number;
  cofinsCents: number;
  creditSnCents: number;
  approxTaxCents: number;
  /** vNF. */
  invoiceCents: number;
  ibsCbsBaseCents: number;
  ibsUfCents: number;
  ibsMunCents: number;
  ibsCents: number;
  cbsCents: number;
  isCents: number;
};

export type TaxResult = {
  destination: Destination;
  /** indFinal. */
  finalConsumer: boolean;
  items: TaxItemResult[];
  totals: TaxTotals;
};

// ---------- utilitários ----------

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};
const isInt = Number.isSafeInteger;

/** Divisão half-up de não negativos. */
const roundDiv = (num: bigint, den: bigint) => (2n * num + den) / (2n * den);

function pct(v: number | null | undefined, what: string, max = HUNDRED): number {
  if (v === null || v === undefined) return 0;
  if (!isInt(v) || v < 0 || v > max)
    fail("tax_rule_invalid", `${what}: percentual fora de 0% a ${max / 10_000}%`);
  return v;
}
function money(v: number, what: string, max = MAX_DOCUMENT_CENTS): number {
  if (!isInt(v) || v < 0 || v > max) fail("invalid_money", `${what} fora do limite`);
  return v;
}
/** Valor × (100% − redução). */
const reduce = (v: number, red: number) => (red ? applyPct(v, HUNDRED - red) : v);
/**
 * Rateio do desconto do documento (05 §3) sem passar do líquido de nenhum item: o piso proporcional de
 * `allocateProportional` e a sobra de centavos no maior item, até o limite dele; o que não couber vai aos seguintes por
 * ordem de valor (empate = o primeiro). Sem isso a sobra (até n − 1 centavos) podia deixar vDesc > vProd − desconto do
 * item e bases negativas quando o desconto chega perto de 100%. Exige `amount` ≤ Σ `caps`.
 */
function allocateCapped(amount: number, caps: readonly number[]): number[] {
  const parts = allocateProportional(amount, caps);
  let excess = 0;
  parts.forEach((p, k) => {
    if (p > caps[k]!) {
      excess += p - caps[k]!;
      parts[k] = caps[k]!;
    }
  });
  if (excess === 0) return parts;
  const order = caps.map((_, k) => k).sort((a, b) => caps[b]! - caps[a]! || a - b);
  for (let j = 0; excess > 0; j++) {
    const k = order[j]!;
    const room = Math.min(caps[k]! - parts[k]!, excess);
    parts[k]! += room;
    excess -= room;
  }
  return parts;
}

const sum = (vals: readonly number[]) =>
  toSafe(
    vals.reduce((a, b) => a + BigInt(b), 0n),
    "invalid_money",
  );

const ICMS_CSTS = new Set(["00", "10", "20", "30", "40", "41", "50", "51", "60", "70", "90"]);
const CSOSNS = new Set(["101", "102", "103", "201", "202", "203", "300", "400", "500", "900"]);
const ST_CSTS = new Set(["10", "30", "70"]);
const ST_CSOSNS = new Set(["201", "202", "203"]);
const IPI_TAXED = new Set(["00", "49", "50", "99"]);
const IPI_CSTS = new Set([
  "00",
  "01",
  "02",
  "03",
  "04",
  "05",
  "49",
  "50",
  "51",
  "52",
  "53",
  "54",
  "55",
  "99",
]);
const PIS_NT = new Set(["04", "05", "06", "07", "08", "09"]);
const IBS_NO_VALUES = new Set(["400", "410", "550"]);
const IBS_SUPPORTED = new Set(["000", "200", "510", ...IBS_NO_VALUES]);

export const isIcmsCst = (s: string) => ICMS_CSTS.has(s);
export const isCsosn = (s: string) => CSOSNS.has(s);
export const isIpiCst = (s: string) => IPI_CSTS.has(s);
export const isPisCofinsCst = (s: string) =>
  /^\d{2}$/.test(s) && (["01", "02"].includes(s) || PIS_NT.has(s) || (Number(s) >= 49 && Number(s) <= 99));
export const isIbsCbsCst = (s: string) => IBS_SUPPORTED.has(s);

// ---------- escolha da regra ----------

export type TaxRuleMatch = {
  company: Pick<TaxCompany, "crt">;
  recipient: TaxRecipient;
  item: Pick<TaxItemInput, "productId" | "productGroupId" | "ncm" | "origin">;
};

const CONDITION_KEYS = [
  "destUf",
  "destIeIndicator",
  "destFinalConsumer",
  "crt",
  "ncmPrefix",
  "productId",
  "productGroupId",
  "productOrigin",
] as const satisfies readonly (keyof TaxRuleConditions)[];

/** Não contribuinte (IE 9) é sempre consumidor final (rejeição 696). */
export const effectiveFinalConsumer = (r: TaxRecipient) => r.finalConsumer || r.ieIndicator === 9;

function ruleMatches(r: TaxRuleConditions & { active: boolean }, m: TaxRuleMatch): boolean {
  if (!r.active) return false;
  const final = effectiveFinalConsumer(m.recipient);
  return (
    (r.destUf === null || r.destUf === m.recipient.uf) &&
    (r.destIeIndicator === null || r.destIeIndicator === m.recipient.ieIndicator) &&
    (r.destFinalConsumer === null || r.destFinalConsumer === final) &&
    (r.crt === null || r.crt === m.company.crt) &&
    (r.ncmPrefix === null || (m.item.ncm !== null && m.item.ncm.startsWith(r.ncmPrefix))) &&
    (r.productId === null || r.productId === m.item.productId) &&
    (r.productGroupId === null || r.productGroupId === m.item.productGroupId) &&
    (r.productOrigin === null || r.productOrigin === m.item.origin)
  );
}

const specificity = (r: TaxRuleConditions) => CONDITION_KEYS.filter((k) => r[k] !== null).length;

/**
 * Regra da natureza para o item (05 §8.1): entre as ativas cujas condições batem (null = qualquer), a de maior
 * `priority`; no empate, a mais específica (mais condições), depois o prefixo de NCM mais longo, depois a que veio
 * primeiro na lista (o DO entrega por data de criação). Nenhuma = null.
 */
export function selectTaxRule<R extends TaxRuleConditions & { priority: number; active: boolean }>(
  rules: readonly R[],
  m: TaxRuleMatch,
): R | null {
  let best: R | null = null;
  let bestKey: [number, number, number] = [0, 0, 0];
  for (const r of rules) {
    if (!ruleMatches(r, m)) continue;
    const key: [number, number, number] = [r.priority, specificity(r), r.ncmPrefix?.length ?? 0];
    if (
      best === null ||
      key[0] > bestKey[0] ||
      (key[0] === bestKey[0] && (key[1] > bestKey[1] || (key[1] === bestKey[1] && key[2] > bestKey[2])))
    ) {
      best = r;
      bestKey = key;
    }
  }
  return best;
}

// ---------- validação da entrada ----------

function assertInput(i: TaxInput): void {
  const { company, recipient } = i;
  if (!CRTS.includes(company.crt)) fail("validation", "CRT da empresa inválido");
  if (!isUf(company.uf)) fail("company_uf_required", "Informe a UF da empresa no perfil fiscal");
  if (recipient.uf !== EXTERIOR && !isUf(recipient.uf)) fail("validation", "UF do destinatário inválida");
  if (!IE_INDICATORS.includes(recipient.ieIndicator))
    fail("validation", "Indicador de IE do destinatário inválido");
  if (i.items.length === 0) fail("validation", "Informe ao menos um item");
  if (i.items.length > MAX_TAX_ITEMS) fail("too_many_items", `No máximo ${MAX_TAX_ITEMS} itens`);
  money(i.freightCents, "Frete");
  money(i.insuranceCents, "Seguro");
  money(i.otherCents, "Outras despesas");
  money(i.discountCents, "Desconto");
  i.items.forEach((it, k) => {
    const n = k + 1;
    if (!isInt(it.qtyQ4) || it.qtyQ4 <= 0 || it.qtyQ4 > MAX_STOCK_QTY_Q4)
      fail("invalid_qty", `Item ${n}: quantidade deve ser positiva e dentro do limite`);
    money(it.unitPriceCents, `Item ${n}: preço`, MAX_PRICE_CENTS);
    money(it.discountCents, `Item ${n}: desconto`);
    if (!isInt(it.origin) || it.origin < 0 || it.origin > 8) fail("validation", `Item ${n}: origem de 0 a 8`);
    if (it.ncm !== null && !/^\d{8}$/.test(it.ncm)) fail("validation", `Item ${n}: NCM com 8 dígitos`);
    if (it.ipiFixedCents !== null) money(it.ipiFixedCents, `Item ${n}: IPI por unidade`, MAX_PRICE_CENTS);
    if (it.stRetained) {
      money(it.stRetained.baseCents, `Item ${n}: base do ST retido`, MAX_PRICE_CENTS);
      money(it.stRetained.stCents, `Item ${n}: ICMS-ST retido`, MAX_PRICE_CENTS);
      money(it.stRetained.substituteOwnCents, `Item ${n}: ICMS do substituto`, MAX_PRICE_CENTS);
    }
    pct(it.approxTaxPct4, `Item ${n}: tributos aproximados`);
  });
}

// ---------- cálculo por item ----------

type Ctx = {
  company: TaxCompany;
  recipient: TaxRecipient;
  direction: TaxDirection;
  dest: Destination;
  final: boolean;
};

type Values = { gross: number; discount: number; freight: number; insurance: number; other: number };

/** Base da operação (sem IPI): vProd + frete + seguro + outras − desconto. */
const opBase = (v: Values) => v.gross + v.freight + v.insurance + v.other - v.discount;

const emptyIcms = (origin: number, cst: string | null, csosn: string | null, group: string): IcmsResult => ({
  group,
  origin,
  cst,
  csosn,
  modBc: null,
  baseCents: 0,
  reductionPct4: 0,
  ratePct4: 0,
  operationCents: 0,
  deferralPct4: 0,
  deferredCents: 0,
  valueCents: 0,
  fcpBaseCents: 0,
  fcpPct4: 0,
  fcpCents: 0,
  creditSnPct4: 0,
  creditSnCents: 0,
  st: null,
  stRetained: null,
});

/** Grupo do leiaute pelo CST/CSOSN (CST 41 e 50 vão no ICMS40; CSOSN 102/103/300/400 no ICMSSN102; 202/203 no 202). */
function icmsGroup(cst: string | null, csosn: string | null): string {
  if (cst !== null) return ["41", "50"].includes(cst) ? "ICMS40" : `ICMS${cst}`;
  if (["102", "103", "300", "400"].includes(csosn!)) return "ICMSSN102";
  if (csosn === "203") return "ICMSSN202";
  return `ICMSSN${csosn}`;
}

function ownRatePct4(c: Ctx, rule: TaxRuleResult, origin: number): number {
  if (c.dest === 2) return interstateRatePct4(c.company.uf, c.recipient.uf as Uf, origin);
  return rule.icmsPct4 !== null
    ? pct(rule.icmsPct4, "Alíquota do ICMS")
    : ICMS_INTERNAL_RATE_PCT4[c.company.uf];
}

/** UF cuja alíquota interna vale para ST e DIFAL: a do destinatário (ou a da empresa no exterior). */
const internalUf = (c: Ctx): Uf => (c.recipient.uf === EXTERIOR ? c.company.uf : c.recipient.uf);

function computeSt(
  c: Ctx,
  rule: TaxRuleResult,
  base: number,
  ipi: number,
  ownDeducted: number,
  ownRate: number,
): StResult {
  const modBc = rule.icmsStModBc ?? 4;
  const rate =
    rule.icmsStPct4 !== null
      ? pct(rule.icmsStPct4, "Alíquota do ICMS-ST")
      : ICMS_INTERNAL_RATE_PCT4[internalUf(c)];
  let mva = 0;
  let adjusted = false;
  if (modBc === 4) {
    if (rule.icmsStMvaPct4 === null) fail("tax_rule_invalid", "Informe a MVA da substituição tributária");
    mva = pct(rule.icmsStMvaPct4, "MVA", MAX_MVA_PCT4);
    // Conv. ICMS 142/2018 cl. 13ª: só quando a alíquota interna do destino é maior que a interestadual.
    if (rule.icmsStMvaAdjust && c.dest === 2 && rate > ownRate) {
      if (rate >= HUNDRED)
        fail("tax_rule_invalid", "MVA ajustada: a alíquota interna do ST deve ser menor que 100%");
      const num = BigInt(HUNDRED + mva) * BigInt(HUNDRED - ownRate);
      mva = Number(roundDiv(num, BigInt(HUNDRED - rate))) - HUNDRED;
      adjusted = true;
    }
  }
  const red = pct(rule.icmsStReductionPct4, "Redução da base do ST");
  const start = base + ipi;
  const stBase = reduce(start + (mva ? applyPct(start, mva) : 0), red);
  const fcpPct = pct(rule.fcpStPct4, "FCP-ST");
  return {
    modBc,
    mvaPct4: mva,
    mvaAdjusted: adjusted,
    reductionPct4: red,
    baseCents: stBase,
    ratePct4: rate,
    valueCents: Math.max(0, applyPct(stBase, rate) - ownDeducted),
    ownDeductedCents: ownDeducted,
    fcpBaseCents: fcpPct ? stBase : 0,
    fcpPct4: fcpPct,
    fcpCents: applyPct(stBase, fcpPct),
  };
}

function computeStRetained(item: TaxItemInput): StRetainedResult | null {
  const r = item.stRetained;
  if (!r) return null;
  const base = mulQ4(r.baseCents, item.qtyQ4);
  const st = mulQ4(r.stCents, item.qtyQ4);
  const own = mulQ4(r.substituteOwnCents, item.qtyQ4);
  return {
    baseCents: base,
    ratePct4: base ? Number(roundDiv(BigInt(st + own) * HUNDRED_N, BigInt(base))) : 0,
    substituteOwnCents: own,
    valueCents: st,
  };
}

function computeIcms(
  c: Ctx,
  rule: TaxRuleResult,
  item: TaxItemInput,
  v: Values,
  ipi: number,
  warnings: string[],
): IcmsResult {
  const simples = isSimplesCrt(c.company.crt);
  const cst = simples ? null : rule.icmsCst;
  const csosn = simples ? rule.icmsCsosn : null;
  if (simples && csosn === null)
    fail("tax_rule_invalid", "Empresa do Simples Nacional (CRT 1 ou 4): a regra precisa de CSOSN");
  if (!simples && cst === null)
    fail(
      "tax_rule_invalid",
      "Empresa do regime normal ou com excesso de sublimite (CRT 2 ou 3): a regra precisa de CST",
    );
  if (cst !== null && !ICMS_CSTS.has(cst)) fail("tax_rule_invalid", `CST de ICMS não suportado: ${cst}`);
  if (csosn !== null && !CSOSNS.has(csosn)) fail("tax_rule_invalid", `CSOSN não suportado: ${csosn}`);
  const code = (cst ?? csosn)!;
  const r = emptyIcms(item.origin, cst, csosn, icmsGroup(cst, csosn));
  const base = opBase(v);
  const rate = ownRatePct4(c, rule, item.origin);
  const red = pct(rule.icmsReductionPct4, "Redução da base do ICMS");

  // ICMS próprio destacado: CST 00/10/20/51/70; 90 e CSOSN 900 quando a regra traz alíquota.
  const ownTaxed =
    ["00", "10", "20", "51", "70"].includes(code) ||
    ((code === "90" || code === "900") && rule.icmsPct4 !== null);
  if (ownTaxed) {
    const reduced = ["20", "51", "70", "90", "900"].includes(code) ? red : 0;
    if (red && !reduced) warnings.push(`Redução de base ignorada no CST ${code}`);
    if (code === "20" || code === "70") {
      if (!red) warnings.push(`CST ${code} sem percentual de redução da base`);
    }
    // CF art. 155 §2º XI: o IPI entra na base do ICMS quando o destinatário é consumidor final.
    r.modBc = 3;
    r.reductionPct4 = reduced;
    r.baseCents = reduce(base + (c.final ? ipi : 0), reduced);
    r.ratePct4 = rate;
    const full = applyPct(r.baseCents, rate);
    if (code === "51") {
      r.operationCents = full;
      r.deferralPct4 = pct(rule.icmsDeferralPct4, "Diferimento");
      r.deferredCents = applyPct(full, r.deferralPct4);
      r.valueCents = full - r.deferredCents;
    } else r.valueCents = full;
    // FCP próprio só na operação interna (na interestadual para não contribuinte ele vai no ICMSUFDest).
    if (rule.fcpPct4 && !simples) {
      if (c.dest === 1) {
        r.fcpPct4 = pct(rule.fcpPct4, "FCP");
        r.fcpBaseCents = r.baseCents;
        r.fcpCents = applyPct(r.baseCents, r.fcpPct4);
      } else warnings.push("FCP próprio ignorado fora da operação interna");
    }
  }

  // Crédito do Simples (LC 123 art. 23): CSOSN 101, 201 e 900 com pCredSN, sobre o valor da operação.
  if (["101", "201", "900"].includes(code) && rule.icmsCreditSnPct4) {
    r.creditSnPct4 = pct(rule.icmsCreditSnPct4, "Crédito do Simples");
    r.creditSnCents = applyPct(base, r.creditSnPct4);
  } else if (code === "101" || code === "201")
    warnings.push(`CSOSN ${code} sem percentual de crédito (pCredSN)`);

  // Substituição tributária.
  const hasSt =
    ST_CSTS.has(code) ||
    ST_CSOSNS.has(code) ||
    ((code === "90" || code === "900") && (rule.icmsStModBc !== null || rule.icmsStMvaPct4 !== null));
  if (hasSt) {
    let own: number;
    if (ownTaxed) own = r.valueCents;
    else if (simples || (code === "30" && rule.icmsPct4 !== null)) own = applyPct(base, rate);
    else own = 0; // CST 30 sem alíquota: operação própria isenta, nada a deduzir.
    r.st = computeSt(c, rule, base, ipi, own, rate);
  }

  if (code === "60" || code === "500") r.stRetained = computeStRetained(item);
  return r;
}

function computeDifal(c: Ctx, rule: TaxRuleResult, icms: IcmsResult, origin: number): DifalResult | null {
  // EC 87/2015: interestadual para não contribuinte; Simples (CRT 1 e 4) fora (ADI 5464); CRT 2 recolhe o ICMS pelas
  // regras das demais empresas (LC 123 art. 13-A), DIFAL incluso; só com ICMS próprio destacado.
  if (c.dest !== 2 || c.recipient.ieIndicator !== 9 || isSimplesCrt(c.company.crt)) return null;
  if (!rule.difal || icms.baseCents === 0) return null;
  const destUf = c.recipient.uf as Uf;
  const destRate =
    rule.icmsDestPct4 !== null
      ? pct(rule.icmsDestPct4, "Alíquota interna do destino")
      : ICMS_INTERNAL_RATE_PCT4[destUf];
  const fcpRate = pct(rule.fcpDestPct4, "FCP do destino");
  const inter = interstateRatePct4(c.company.uf, destUf, origin);
  let base = icms.baseCents;
  if (rule.difalDoubleBase) {
    if (destRate + fcpRate >= HUNDRED)
      fail("tax_rule_invalid", "Alíquota do destino + FCP deve ser menor que 100%");
    // Base dupla ("por dentro"): (vBC − ICMS da origem) / (1 − (alíquota interna + FCP do destino)).
    base = Number(
      roundDiv(BigInt(icms.baseCents - icms.valueCents) * HUNDRED_N, BigInt(HUNDRED - destRate - fcpRate)),
    );
  }
  return {
    baseCents: base,
    fcpBaseCents: fcpRate ? base : 0,
    fcpPct4: fcpRate,
    destRatePct4: destRate,
    interRatePct4: inter,
    interPartPct4: HUNDRED,
    fcpCents: applyPct(base, fcpRate),
    destCents: Math.max(0, applyPct(base, destRate) - icms.valueCents),
    originCents: 0,
    doubleBase: rule.difalDoubleBase,
  };
}

function computeIpi(
  rule: TaxRuleResult,
  item: TaxItemInput,
  v: Values,
  warnings: string[],
): IpiResult | null {
  if (rule.ipiCst === null) return null;
  if (!IPI_CSTS.has(rule.ipiCst)) fail("tax_rule_invalid", `CST de IPI não suportado: ${rule.ipiCst}`);
  const r: IpiResult = {
    group: "IPINT",
    cst: rule.ipiCst,
    enq: rule.ipiEnq ?? "999",
    baseCents: 0,
    ratePct4: 0,
    qtyQ4: 0,
    unitCents: 0,
    valueCents: 0,
  };
  if (!IPI_TAXED.has(rule.ipiCst)) return r;
  r.group = "IPITrib";
  if (rule.ipiPct4 === null && item.ipiFixedCents) {
    // Pauta por unidade (qUnid × vUnid).
    r.qtyQ4 = item.qtyQ4;
    r.unitCents = item.ipiFixedCents;
    r.valueCents = mulQ4(item.ipiFixedCents, item.qtyQ4);
    return r;
  }
  if (rule.ipiPct4 === null)
    warnings.push("IPI tributado sem alíquota na regra nem valor por unidade no produto");
  // RIPI art. 190: frete e despesas cobrados integram a base; desconto incondicional não (STF RE 567.935).
  r.baseCents = opBase(v);
  r.ratePct4 = pct(rule.ipiPct4, "Alíquota do IPI");
  r.valueCents = applyPct(r.baseCents, r.ratePct4);
  return r;
}

function pisCofinsGroup(cst: string): PisCofinsResult["group"] {
  if (cst === "01" || cst === "02") return "Aliq";
  if (PIS_NT.has(cst)) return "NT";
  const n = Number(cst);
  if (/^\d{2}$/.test(cst) && n >= 49 && n <= 99) return "Outr";
  return fail("tax_rule_invalid", `CST de PIS/COFINS não suportado: ${cst}`);
}

function computePisCofins(
  c: Ctx,
  rule: TaxRuleResult,
  which: "pis" | "cofins",
  v: Values,
  icms: IcmsResult,
): PisCofinsResult {
  const ruleCst = which === "pis" ? rule.pisCst : rule.cofinsCst;
  const rulePct = which === "pis" ? rule.pisPct4 : rule.cofinsPct4;
  const what = which === "pis" ? "PIS" : "COFINS";
  // Simples Nacional (inclusive CRT 2): recolhido no DAS, sem destaque.
  if (federalSimples(c.company.crt)) {
    const cst = ruleCst ?? "49";
    return { group: pisCofinsGroup(cst), cst, baseCents: 0, ratePct4: 0, valueCents: 0 };
  }
  const cst = ruleCst ?? "01";
  const group = pisCofinsGroup(cst);
  if (group === "NT") return { group, cst, baseCents: 0, ratePct4: 0, valueCents: 0 };
  // Sem alíquota na regra: Lucro Real = não cumulativo (1,65%/7,6%); Presumido (ou em branco) = cumulativo (0,65%/3%).
  const real = c.company.taxRegime === "real";
  const fallback =
    group === "Aliq" ? (which === "pis" ? (real ? 16_500 : 6_500) : real ? 76_000 : 30_000) : 0;
  const rate = rulePct !== null ? pct(rulePct, what) : fallback;
  if (group === "Outr" && rate === 0) return { group, cst, baseCents: 0, ratePct4: 0, valueCents: 0 };
  const base = Math.max(0, opBase(v) - (rule.pisCofinsExcludeIcms ? icms.valueCents : 0));
  return { group, cst, baseCents: base, ratePct4: rate, valueCents: applyPct(base, rate) };
}

function ibsPart(base: number, rate: number, reduction: number, deferral: number): IbsCbsPart {
  const effective = reduction
    ? Number(roundDiv(BigInt(rate) * BigInt(HUNDRED - reduction), HUNDRED_N))
    : rate;
  const full = applyPct(base, effective);
  const deferred = applyPct(full, deferral);
  return {
    ratePct4: rate,
    reductionPct4: reduction,
    effectivePct4: effective,
    deferralPct4: deferral,
    deferredCents: deferred,
    valueCents: full - deferred,
  };
}

function computeIbsCbs(
  rule: TaxRuleResult,
  item: TaxItemInput,
  v: Values,
  icms: IcmsResult,
  difal: DifalResult | null,
  pis: PisCofinsResult,
  cofins: PisCofinsResult,
  warnings: string[],
): IbsCbsResult | null {
  if (rule.ibsCst === null) return null;
  let cst = rule.ibsCst;
  let classCode = rule.ibsClass;
  // A classificação do produto refina a tributação integral da regra (o CST são os 3 primeiros dígitos do cClassTrib).
  if (cst === "000" && item.ibsCbsClass) {
    classCode = item.ibsCbsClass;
    cst = classCode.slice(0, 3);
  }
  if (classCode === null && cst === "000") classCode = "000001";
  if (!IBS_SUPPORTED.has(cst)) fail("tax_rule_invalid", `CST de IBS/CBS não suportado: ${cst}`);
  if (classCode === null)
    return fail("tax_rule_invalid", "Informe a classificação tributária (cClassTrib) do IBS/CBS");
  if (!/^\d{6}$/.test(classCode) || classCode.slice(0, 3) !== cst)
    fail("tax_rule_invalid", `Classificação tributária ${classCode} não corresponde ao CST ${cst}`);
  const zero = ibsPart(0, 0, 0, 0);
  if (IBS_NO_VALUES.has(cst))
    return {
      cst,
      classCode,
      hasValues: false,
      baseCents: 0,
      ibsUf: zero,
      ibsMun: zero,
      ibsCents: 0,
      cbs: zero,
    };
  // NT 2025.002 (transição): vProd + frete + seguro + outras − desconto − PIS − COFINS − ICMS − ICMSUFDest − FCP.
  const base = Math.max(
    0,
    opBase(v) -
      pis.valueCents -
      cofins.valueCents -
      icms.valueCents -
      icms.fcpCents -
      (difal ? difal.destCents + difal.fcpCents : 0),
  );
  const ibsRed = cst === "200" ? pct(rule.ibsReductionPct4, "Redução do IBS") : 0;
  const cbsRed = cst === "200" ? pct(rule.cbsReductionPct4, "Redução da CBS") : 0;
  if (cst === "200" && !ibsRed && !cbsRed) warnings.push("CST 200 de IBS/CBS sem percentual de redução");
  const dif = cst === "510" ? pct(rule.ibsDeferralPct4, "Diferimento do IBS/CBS") : 0;
  const ufRate = rule.ibsUfPct4 !== null ? pct(rule.ibsUfPct4, "IBS UF") : REFORM_2026_RATES.ibsUfPct4;
  const munRate =
    rule.ibsMunPct4 !== null ? pct(rule.ibsMunPct4, "IBS Município") : REFORM_2026_RATES.ibsMunPct4;
  const cbsRate = rule.cbsPct4 !== null ? pct(rule.cbsPct4, "CBS") : REFORM_2026_RATES.cbsPct4;
  const ibsUf = ibsPart(base, ufRate, ibsRed, dif);
  const ibsMun = ibsPart(base, munRate, ibsRed, dif);
  return {
    cst,
    classCode,
    hasValues: true,
    baseCents: base,
    ibsUf,
    ibsMun,
    ibsCents: ibsUf.valueCents + ibsMun.valueCents,
    cbs: ibsPart(base, cbsRate, cbsRed, dif),
  };
}

function computeItem(c: Ctx, rule: TaxRule, item: TaxItemInput, v: Values, index: number): TaxItemResult {
  const warnings: string[] = [];
  const ipi = computeIpi(rule, item, v, warnings);
  const icms = computeIcms(c, rule, item, v, ipi?.valueCents ?? 0, warnings);
  const difal = computeDifal(c, rule, icms, item.origin);
  const pis = computePisCofins(c, rule, "pis", v, icms);
  const cofins = computePisCofins(c, rule, "cofins", v, icms);
  const ibsCbs = computeIbsCbs(rule, item, v, icms, difal, pis, cofins, warnings);
  const isRate = pct(rule.isPct4, "Imposto Seletivo");
  const is = isRate ? { baseCents: ibsCbs?.baseCents ?? opBase(v), ratePct4: isRate, valueCents: 0 } : null;
  if (is) is.valueCents = applyPct(is.baseCents, isRate);
  if (c.dest === 3 && icms.valueCents > 0)
    warnings.push("Exportação com ICMS destacado: confira o CST da regra");
  const total = sum([
    v.gross - v.discount,
    v.freight,
    v.insurance,
    v.other,
    icms.st?.valueCents ?? 0,
    icms.st?.fcpCents ?? 0,
    ipi?.valueCents ?? 0,
  ]);
  return {
    index,
    ruleId: rule.id,
    cfop: adjustCfop(rule.cfop, c.direction, c.dest, c.recipient.ieIndicator),
    grossCents: v.gross,
    discountCents: v.discount,
    freightCents: v.freight,
    insuranceCents: v.insurance,
    otherCents: v.other,
    icms,
    difal,
    ipi,
    pis,
    cofins,
    ibsCbs,
    is,
    approxTaxCents: applyPct(v.gross - v.discount, pct(item.approxTaxPct4, "Tributos aproximados")),
    totalCents: total,
    infoText: rule.infoText,
    warnings,
  };
}

export function taxTotalsOf(items: readonly TaxItemResult[]): TaxTotals {
  const s = (f: (i: TaxItemResult) => number) => sum(items.map(f));
  return {
    baseCents: s((i) => i.icms.baseCents),
    icmsCents: s((i) => i.icms.valueCents),
    fcpCents: s((i) => i.icms.fcpCents),
    stBaseCents: s((i) => i.icms.st?.baseCents ?? 0),
    stCents: s((i) => i.icms.st?.valueCents ?? 0),
    fcpStCents: s((i) => i.icms.st?.fcpCents ?? 0),
    difalCents: s((i) => i.difal?.destCents ?? 0),
    difalFcpCents: s((i) => i.difal?.fcpCents ?? 0),
    difalOriginCents: s((i) => i.difal?.originCents ?? 0),
    productsCents: s((i) => i.grossCents),
    freightCents: s((i) => i.freightCents),
    insuranceCents: s((i) => i.insuranceCents),
    discountCents: s((i) => i.discountCents),
    otherCents: s((i) => i.otherCents),
    ipiCents: s((i) => i.ipi?.valueCents ?? 0),
    pisCents: s((i) => i.pis.valueCents),
    cofinsCents: s((i) => i.cofins.valueCents),
    creditSnCents: s((i) => i.icms.creditSnCents),
    approxTaxCents: s((i) => i.approxTaxCents),
    invoiceCents: s((i) => i.totalCents),
    ibsCbsBaseCents: s((i) => i.ibsCbs?.baseCents ?? 0),
    ibsUfCents: s((i) => i.ibsCbs?.ibsUf.valueCents ?? 0),
    ibsMunCents: s((i) => i.ibsCbs?.ibsMun.valueCents ?? 0),
    ibsCents: s((i) => i.ibsCbs?.ibsCents ?? 0),
    cbsCents: s((i) => i.ibsCbs?.cbs.valueCents ?? 0),
    isCents: s((i) => i.is?.valueCents ?? 0),
  };
}

/**
 * Impostos da nota item a item (05 §8). Desconto, frete, seguro e outras despesas do documento são rateados pelo total
 * líquido do item (05 §3, sobra de centavos no maior item). Sem regra para algum item = `tax_rule_missing`.
 */
export function computeTaxes(input: TaxInput): TaxResult {
  assertInput(input);
  const c: Ctx = {
    company: input.company,
    recipient: input.recipient,
    direction: input.direction,
    dest: destinationOf(input.company.uf, input.recipient.uf),
    final: effectiveFinalConsumer(input.recipient),
  };
  const grosses = input.items.map((it) => mulQ4(it.unitPriceCents, it.qtyQ4));
  const nets = input.items.map((it, k) => {
    if (grosses[k]! > MAX_DOCUMENT_CENTS) fail("total_limit", `Item ${k + 1}: valor acima do limite da nota`);
    if (it.discountCents > grosses[k]!)
      fail("invalid_discount", `Item ${k + 1}: o desconto não pode passar do valor do item`);
    return grosses[k]! - it.discountCents;
  });
  const netTotal = nets.reduce((a, b) => a + BigInt(b), 0n);
  if (netTotal > BigInt(MAX_DOCUMENT_CENTS)) fail("total_limit", "Total da nota acima do limite");
  if (BigInt(input.discountCents) > netTotal)
    fail("invalid_discount", "O desconto não pode passar do total dos itens");
  const share = (amount: number) => allocateProportional(amount, nets);
  const discount = allocateCapped(input.discountCents, nets);
  const freight = share(input.freightCents);
  const insurance = share(input.insuranceCents);
  const other = share(input.otherCents);
  const items = input.items.map((it, k) => {
    const rule = selectTaxRule(input.rules, { company: input.company, recipient: input.recipient, item: it });
    if (!rule)
      fail(
        "tax_rule_missing",
        `Nenhuma regra da natureza de operação atende o item ${k + 1}` +
          (it.ncm ? ` (NCM ${it.ncm})` : " (sem NCM)") +
          ` para ${input.recipient.uf}. Cadastre uma regra que cubra este caso.`,
      );
    return computeItem(
      c,
      rule!,
      it,
      {
        gross: grosses[k]!,
        discount: it.discountCents + discount[k]!,
        freight: freight[k]!,
        insurance: insurance[k]!,
        other: other[k]!,
      },
      k,
    );
  });
  return { destination: c.dest, finalConsumer: c.final, items, totals: taxTotalsOf(items) };
}

/** UFs aceitas no destino das regras (as 27 + exterior). */
export const TAX_DEST_UFS: readonly DestUf[] = [...BRAZIL_UFS, EXTERIOR];
